import { Grid, Tooltip } from '@material-ui/core';
import Avatar from '@material-ui/core/Avatar/Avatar';
import LinearProgress from '@material-ui/core/LinearProgress';
import LoyaltyIcon from '@mui/icons-material/Loyalty';
import moment from 'moment';
import React, { Fragment, useEffect, useState } from 'react';
import Helmet from 'react-helmet';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useHistory, useParams, Redirect } from 'react-router-dom';
import { calculateCurrencies } from '@joindeed/currency';
import { Alert } from '@mui/material';

import ExternalLinkIcon from 'assets/icons/external-link.svg';
import DonationDisbursementTooltip from 'retired/DonationDisbursementTooltip';
import DonationMatchProcessingTooltip from 'retired/DonationMatchProcessingTooltip';
import H4 from 'retired/H4';
import Img from 'retired/Img';
import NonprofitLink from 'retired/NonprofitLink';
import PageHeaderRedesign from 'retired/PageHeaderRedesign';
import { SummaryCard } from 'retired/SummaryCard';
import { SummaryCardButton } from 'retired/SummaryCard/SummaryCardButton';
import { SummaryCardHeader } from 'retired/SummaryCard/SummaryCardHeader';
import { SummaryCardImage } from 'retired/SummaryCard/SummaryCardImage';
import { SummarySegment } from 'retired/SummaryCard/SummarySegment';
import Chip from 'retired/Chip/index';
import { selectBudgetAllocations } from 'entities/budgetAllocation/selectors';
import { selectDonationById } from 'entities/donation/selectors';
import { selectDonationMatchingRules } from 'entities/donationMatchingRule/selectors';
import { selectOrganization, selectUser } from 'entities/user/selectors';
import User from 'entities/user/model';
import { dark, lightViolet } from 'utils/color-styles';
import { currenciesFormat, CurrencyCode } from 'utils/currencyFormat';
import { useInjectEpics } from 'utils/injectEpics';
import { useInjectReducer } from 'utils/injectReducer';
import { getAppDomain } from 'utils/getAppDomain';
import { Privileges } from 'utils/privileges';
import { DonationPayrollStatus } from 'containers/PayrollApiPage/DonationPayrollStatus';
import { DonatePageSection, DonatePageSections } from 'components/DonatePageSection';
import { stripTime } from 'utils/stripTime';
import type { State } from 'reducers';
import { ClickableUrl } from 'utils/urls';
import { truthy } from 'utils/truthy';
import { DonationFrequency } from 'generated/graphql';

import * as Actions from './actions';
import epics from './epics';
import reducer from './reducer';
import { selectLoading, selectError } from './selectors';

const momentFormat = 'MMM DD, YYYY';

const styles = {
  headStyle: {
    fontSize: 15,
    fontWeight: 500,
  },
  textStyle: {
    fontSize: 15,
    fontWeight: 300,
  },
  linkStyle: {
    display: 'block',
    fontSize: 15,
    fontWeight: 300,
    color: dark,
    textDecoration: 'underline',
  },
};

const key = 'donationPage';

const UserLink = ({ user }: { user: User }): JSX.Element => {
  const { name, mainPicture, id } = user;

  // @NOTE-GG: Link to to user profile if a corporate donation is associated with an __t: User
  const organizerOrVolunteer = user.type === 'User' ? 'volunteers' : 'organizers';

  return (
    <div style={{ marginBottom: 20, marginTop: 20 }}>
      <Avatar
        alt={name}
        src={mainPicture.replace('http://', 'https://')}
        style={{
          width: 40,
          height: 40,
          float: 'left',
          marginRight: 10,
        }}
      />
      <Link
        to={`/${organizerOrVolunteer}/${id}`}
        style={{
          ...styles.linkStyle,
          lineHeight: '40px',
        }}
      >
        {name}
      </Link>
    </div>
  );
};

const DonationPage = (): JSX.Element => {
  useInjectReducer({
    key,
    reducer,
  });
  useInjectEpics({
    key,
    epics,
  });

  const { id: donationId } = useParams<{ id: string }>();

  const donation = useSelector((state: State) => selectDonationById(state, donationId));

  const loading = useSelector(selectLoading);
  const error = useSelector(selectError);
  const organization = useSelector(selectOrganization);
  const user = useSelector(selectUser);
  const donationMatchingRules = useSelector(selectDonationMatchingRules);
  const budgetAllocations = useSelector(selectBudgetAllocations);

  const dispatch = useDispatch();

  const [hasAttemptedLoad, setHasAttemptedLoad] = useState(false);

  useEffect(() => {
    if (user && donationId) {
      dispatch(Actions.initAction(donationId));
      setHasAttemptedLoad(true);
    }
  }, [user, donationId]);

  const history = useHistory();

  useEffect(() => {
    if (user && !user.hasPrivilege(Privileges.DONATION_VIEW)) {
      history.replace('/');
    }
  }, [user]);

  if (error) {
    return (
      <Alert severity="error">
        There was an error fetching a donation
        <br />
        <br />
        {error?.message}
      </Alert>
    );
  }

  if (!user || loading || (!donation && !hasAttemptedLoad)) {
    return <LinearProgress />;
  }

  if (!donation && !loading && hasAttemptedLoad) {
    return <Redirect to="/404" />;
  }

  const isCompany = organization && organization.isCompany();

  const isPayrollDonation = donation.paymentMethodDetails?.type === 'Payroll';

  const matchedRule =
    donation.matchedRule &&
    donationMatchingRules.find((matchingRule) => matchingRule?.get('id') === donation.matchedRule);

  const corporateDonationBudgetAllocation =
    donation.budgetAllocation &&
    budgetAllocations.find(
      (budgetAllocation) => budgetAllocation?.get('id') === donation.budgetAllocation
    );

  const trackingUrl = donation.donationTrackingURL;
  const matchtrackingUrl = donation.matchTrackingURL;
  // @NOTE-AC: Somehow, as of 2024-01-12, `api` sends `donation.feeAmount` but not `donation.total`
  const total = donation.total || donation.amount + (donation.feeAmount || 0);

  const totalCurrencies =
    !!total &&
    !!donation.amountCurrencies &&
    calculateCurrencies(total, donation.currency, donation.amountCurrencies);

  const feeAmountCurrencies =
    !!donation.feeAmount &&
    !!donation.amountCurrencies &&
    calculateCurrencies(donation.feeAmount, donation.currency, donation.amountCurrencies);

  const donor = donation.isCorporateDonation() ? donation.submitter : donation.user;

  return (
    <>
      <Helmet title={donation.description} />
      <PageHeaderRedesign
        title={donation.description}
        onArrowClick={() =>
          history.length > 0
            ? history.goBack()
            : history.push(donation.isCorporateDonation() ? '/donations/corporate' : '/donations')
        }
      />
      <div
        style={{
          marginLeft: 20,
          marginRight: 20,
        }}
      >
        <Grid container spacing={10}>
          <Grid item md={donation.isCorporateDonation() ? 8 : 12}>
            <div
              style={{
                flexGrow: 1,
                paddingTop: 0,
                marginBottom: 30,
                borderRadius: 12,
                border: '1px solid rgba(230, 230, 230, 1)',
                padding: 32,
                backgroundColor: 'white',
              }}
            >
              <div style={{ width: '100%' }}>
                <a
                  style={{ float: 'right' }}
                  target="_blank"
                  href={`${getAppDomain([organization, user])}/donations/${donation.id}`}
                  rel="noreferrer"
                >
                  View in app{' '}
                  <Img
                    src={ExternalLinkIcon}
                    alt="External Link"
                    style={{
                      width: 16,
                      height: 16,
                      marginLeft: 5,
                    }}
                  />
                </a>

                <H4>Donation details</H4>

                {donor ? <UserLink user={donor} /> : null}

                <DonatePageSections>
                  {[
                    donation.deed && donation.deed.id && organization && (
                      <DonatePageSection
                        key="deed"
                        title="Deed"
                        value={
                          donation.deed.organization?.id === organization.id ||
                          donation.deed.partner?.id === organization.id ? (
                            <Link
                              to={`/deeds/${donation.deed.pathSlug}/${donation.deed.id}`}
                              style={styles.linkStyle}
                            >
                              {donation.deedName}
                            </Link>
                          ) : (
                            <div style={styles.textStyle}>{donation.deedName}</div>
                          )
                        }
                      />
                    ),

                    isCompany &&
                      (donation.nonprofits.size > 0 ||
                        donation.paymentMethodDetails?.additionalData?.nonprofit?.externalName) && (
                        <DonatePageSection
                          key="nonprofit"
                          title="Nonprofit"
                          value={
                            <>
                              {' '}
                              {donation?.nonprofits?.map(
                                (nonprofit, i) =>
                                  nonprofit && (
                                    <Fragment key={nonprofit.id}>
                                      <NonprofitLink nonprofit={nonprofit} />
                                      {i + 1 < donation.nonprofits.size ? ', ' : ''}
                                    </Fragment>
                                  )
                              )}
                              {
                                donation.paymentMethodDetails?.additionalData?.nonprofit
                                  ?.externalName
                              }
                            </>
                          }
                          tooltip={
                            donation.corporateProgram ? (
                              <Chip
                                label={donation.corporateProgram}
                                size="small"
                                icon={
                                  <Tooltip title="Corporate Program">
                                    <LoyaltyIcon fontSize="small" />
                                  </Tooltip>
                                }
                              />
                            ) : null
                          }
                        />
                      ),

                    <DonatePageSection
                      key="total"
                      title="Total payment amount"
                      value={
                        <>
                          {' '}
                          {totalCurrencies
                            ? currenciesFormat(
                                totalCurrencies,
                                user.displayCurrency as CurrencyCode
                              )
                            : '-'}
                          {totalCurrencies && user.displayCurrency !== donation.currency
                            ? ` (originally ${currenciesFormat(
                                totalCurrencies,
                                donation.currency
                              )})`
                            : ''}
                        </>
                      }
                    />,

                    <DonatePageSection
                      key="amount"
                      title="Donation amount"
                      value={
                        <>
                          {' '}
                          {donation.amountCurrencies
                            ? currenciesFormat(
                                donation.amountCurrencies,
                                user.displayCurrency as CurrencyCode
                              )
                            : '-'}
                          {user.displayCurrency !== donation.currency
                            ? ` (originally ${currenciesFormat(
                                donation.amountCurrencies,
                                donation.currency
                              )})`
                            : ''}
                        </>
                      }
                    />,

                    isPayrollDonation && (
                      <DonatePageSection
                        key="feeAmount"
                        title="Processing fee"
                        value={
                          <>
                            {' '}
                            {feeAmountCurrencies
                              ? currenciesFormat(
                                  feeAmountCurrencies,
                                  user.displayCurrency as CurrencyCode
                                )
                              : '-'}
                            {feeAmountCurrencies && user.displayCurrency !== donation.currency
                              ? ` (originally ${currenciesFormat(
                                  feeAmountCurrencies,
                                  donation.currency
                                )})`
                              : ''}
                          </>
                        }
                      />
                    ),

                    <DonatePageSection title="Donation processor" value={donation.providerName} />,

                    isPayrollDonation && (
                      <DonatePageSection
                        key="payroll-deduction"
                        title="Payroll deduction status"
                        value={
                          <div style={{ marginTop: 10 }}>
                            {donation?.payrollStatus === 'Failure' ? (
                              <DonationPayrollStatus
                                status="Failure"
                                executionErrorMessage={donation.getExecutionErrorMessage()}
                              />
                            ) : (
                              <span
                                style={{
                                  height: 26,
                                  verticalAlign: 'top',
                                  backgroundColor: '#FFF2DC',
                                  padding: 5,
                                  fontSize: 11,
                                  borderRadius: 5,
                                }}
                              >
                                {donation.payrollStatus}
                              </span>
                            )}
                          </div>
                        }
                      />
                    ),

                    <DonatePageSection
                      key="payment-method"
                      title="Payment method"
                      value={
                        <>
                          {' '}
                          {donation.paymentMethodDetailsType}
                          {isCompany &&
                            donation.isExternalMatchRequest() &&
                            donation.receiptUrl && (
                              <span>
                                {' - '}
                                <a
                                  href={donation.receiptUrl}
                                  className="hoverable"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  View uploaded receipt
                                </a>
                              </span>
                            )}
                        </>
                      }
                    />,

                    corporateDonationBudgetAllocation && (
                      <DonatePageSection
                        key="budget-rule"
                        title="Budget rule"
                        value={corporateDonationBudgetAllocation?.get('title')}
                      />
                    ),

                    <DonatePageSection
                      key="frequency"
                      title="Frequency"
                      value={
                        donation?.donationSchedule &&
                        donation.donationSchedule.frequency !== DonationFrequency.OneTime
                          ? 'Recurring'
                          : 'One-Time'
                      }
                    />,

                    donation?.payrollRun && (
                      <DonatePageSection
                        key="deduction-date"
                        title="Payroll Deduction Date"
                        value={`${moment(stripTime(donation.payrollRun?.endDate)).format(
                          momentFormat
                        )} (${moment(stripTime(donation.payrollRun?.payDate)).format(
                          momentFormat
                        )})`}
                      />
                    ),

                    <DonatePageSection
                      key="donated-on"
                      title="Donated on"
                      value={moment(donation.date).format(momentFormat)}
                    />,

                    !donation.isExternal() && (
                      <DonatePageSection
                        key="processed-on"
                        title="Processed on"
                        value={
                          // eslint-disable-next-line no-nested-ternary
                          donation.processedAt
                            ? moment(donation.processedAt).format(momentFormat)
                            : donation.estimatedProcessedAt
                            ? moment(donation.estimatedProcessedAt).format(momentFormat)
                            : '-'
                        }
                        tooltip={
                          donation.estimatedProcessedAt ? (
                            <DonationMatchProcessingTooltip providerName={donation.providerName} />
                          ) : null
                        }
                      />
                    ),

                    !donation.isExternal() && !donation.processedAt && (
                      <DonatePageSection
                        key="disbursed-on"
                        title="Estimated disbursement date"
                        value={
                          // eslint-disable-next-line no-nested-ternary
                          donation.disbursedAt
                            ? moment(donation.disbursedAt).format(momentFormat)
                            : donation.estimatedDisbursedAt
                            ? moment(donation.estimatedDisbursedAt).format(momentFormat)
                            : '-'
                        }
                        tooltip={
                          <DonationDisbursementTooltip providerName={donation.providerName} />
                        }
                      />
                    ),

                    donation.matchedAmount && (
                      <DonatePageSection
                        key="match"
                        title="Match"
                        value={
                          <>
                            {donation.matchedAmountCurrencies
                              ? currenciesFormat(
                                  donation.matchedAmountCurrencies,
                                  user.displayCurrency as CurrencyCode
                                )
                              : '-'}
                            {donation.matchedAmount && user.displayCurrency !== donation.currency
                              ? ` (originally ${currenciesFormat(
                                  donation.matchedAmountCurrencies,
                                  donation.currency
                                )})`
                              : ''}
                            {donation.matchedAt &&
                              ` on ${moment(donation.matchedAt).format(momentFormat)}`}
                            {matchedRule && ` - ${matchedRule?.get('title')}`}
                          </>
                        }
                      />
                    ),

                    donation.matchedAmount && (
                      <DonatePageSection
                        key="match-processor"
                        title="Match processor"
                        value={donation.matchProcessedAt ? donation.matchingProvider : '-'}
                      />
                    ),

                    donation.metadata?.matchRequestRejectedAt && (
                      <DonatePageSection
                        title="Rejected at"
                        value={
                          <>
                            {' '}
                            Request denied on{' '}
                            {moment(donation.metadata.matchRequestRejectedAt).format(momentFormat)}
                          </>
                        }
                      />
                    ),

                    donation.metadata?.matchRequestRejectionReason && (
                      <DonatePageSection
                        title="Rejection reason"
                        value={
                          <> Rejection reason {donation.metadata.matchRequestRejectionReason}</>
                        }
                      />
                    ),

                    donation.matchedAmount && donation.matchProcessedAt && (
                      <DonatePageSection
                        key="match-processed-on"
                        title="Match processed on"
                        value={
                          donation.matchProcessedAt
                            ? moment(donation.matchProcessedAt).format(momentFormat)
                            : '-'
                        }
                      />
                    ),

                    donation.matchedAmount && !donation.matchProcessedAt && (
                      <DonatePageSection
                        key="estimated-match-disbursement-date"
                        title="Estimated match disbursement date"
                        value={
                          donation.matchDisbursedAt
                            ? moment(donation.matchDisbursedAt).format(momentFormat)
                            : '-'
                        }
                        tooltip={
                          <DonationDisbursementTooltip
                            providerName={donation.matchingProvider ?? donation.providerName}
                          />
                        }
                      />
                    ),

                    donation.isCorporateDonation() && donation.designation && (
                      <DonatePageSection
                        key="message"
                        title="Message to the nonprofit"
                        value={donation.designation}
                      />
                    ),

                    donation.note && <DonatePageSection title="Note" value={donation.note} />,
                    donation.employeeNote && (
                      <DonatePageSection
                        key="notice"
                        title="Notice from Deed"
                        value={donation.employeeNote}
                      />
                    ),

                    (donation.receiptNumber || donation.identifier) && (
                      <DonatePageSection
                        key="identifier"
                        title="Donation Identifier"
                        value={donation.receiptNumber || donation.identifier}
                      />
                    ),

                    trackingUrl && (
                      <DonatePageSection
                        key="tracking-url"
                        title="Donation Tracking URL"
                        value={<ClickableUrl href={trackingUrl} />}
                      />
                    ),

                    donation.matchDisbursementTransactionIdentifier && (
                      <DonatePageSection
                        key="match-identifier"
                        title="Donation Match Identifier"
                        value={donation.matchDisbursementTransactionIdentifier}
                      />
                    ),
                    matchtrackingUrl && (
                      <DonatePageSection
                        key="match-tracking-url"
                        title="Donation Match Tracking URL"
                        value={<ClickableUrl href={matchtrackingUrl} />}
                      />
                    ),
                  ].filter(truthy)}
                </DonatePageSections>
              </div>
            </div>
          </Grid>
          {donation.isCorporateDonation() && (
            <Grid item md={4}>
              <SummaryCard>
                <SummaryCardHeader
                  headerImage={user?.organization?.mainPicture}
                  title="Thank you"
                />
                <SummaryCardImage
                  src="https://files.godeed.today/1626159965233_Untitled_Artwork1812.png"
                  alt="Thank you for your contribution!"
                  color={lightViolet}
                />
                <SummarySegment
                  title="From"
                  content={donation.nonprofits.map(
                    (nonprofit) =>
                      nonprofit && (
                        <NonprofitLink nonprofit={nonprofit}>
                          <Chip
                            style={{
                              height: 'auto',
                            }}
                            avatar={
                              nonprofit.get('mainPicture') && (
                                <Avatar src={nonprofit.get('mainPicture')} />
                              )
                            }
                            key={nonprofit.get('id')}
                            label={nonprofit.get('name')}
                          />
                        </NonprofitLink>
                      )
                  )}
                />
                {donation.isCorporateBudget() ? (
                  <SummaryCardButton
                    title="Make another donation"
                    onClick={() => history.push(`/donations/corporate/donate`)}
                    last
                    flexContentContainerStyles={{ width: '100%' }}
                  />
                ) : null}
                {donation.isExternal() ? (
                  <SummaryCardButton
                    title="Log another donation"
                    onClick={() => history.push(`/donations/corporate/log`)}
                    last
                    flexContentContainerStyles={{ width: '100%' }}
                  />
                ) : null}
              </SummaryCard>
            </Grid>
          )}
        </Grid>
      </div>
    </>
  );
};

export default DonationPage;
